import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Menu,
  Select,
  Row,
  Col,
  Statistic,
  Layout,
  Tag,
  Progress,
  Alert,
  Dropdown,
} from 'antd';
import {
  CarOutlined,
  LineChartOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  ClusterOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import DateRangeFilter from '../../components/DateRangeFilter';

const { Title, Text } = Typography;
const { Content } = Layout;

const initialDateFilter = {
  year: dayjs().year(),
  month: dayjs().month(),
  // dateRange: undefined, // Only use year/month for initial state
};

const VanReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalVans: 0,
    activeVans: 0,
    totalJobs: 0,
    completedJobs: 0,
    totalMileage: 0,
    maintenanceCount: 0
  });
  const [vans, setVans] = useState([]);
  const [vanStatusDistribution, setVanStatusDistribution] = useState([]);
  const [vanJobDistribution, setVanJobDistribution] = useState([]);

  const fetchVanData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Build params based on dateFilter mode
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined
      };
      if (dateFilter.dateRange) {
        params.startDate = dateFilter.dateRange[0]?.format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1]?.format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      }

      const res = await apiService.reports.getVanReport(params);
      if (!res || !res.data || !res.data.success) {
        throw new Error('No data received from API');
      }

      const { vans, stats, statusDistribution, jobDistribution } = res.data.data;
      
      setVans(vans);
      setStats(stats);
      setVanStatusDistribution(statusDistribution);
      setVanJobDistribution(jobDistribution);
    } catch (err) {
      setError(err.message || 'Failed to fetch van data');
      message.error('Failed to fetch van data');
    } finally {
      setLoading(false);
    }
  }, [dateFilter.year, dateFilter.month, dateFilter.dateRange, statusFilter]);

  useEffect(() => {
    fetchVanData();
  }, [fetchVanData]);

  const handleDateFilterChange = (newValue) => {
    // Only update if the mode or value actually changes
    const modeChanged =
      (!!newValue.dateRange !== !!dateFilter.dateRange) ||
      (newValue.year !== dateFilter.year) ||
      (newValue.month !== dateFilter.month);

    let rangeChanged = false;
    if (newValue.dateRange && dateFilter.dateRange) {
      rangeChanged =
        newValue.dateRange[0]?.format('YYYY-MM-DD') !== dateFilter.dateRange[0]?.format('YYYY-MM-DD') ||
        newValue.dateRange[1]?.format('YYYY-MM-DD') !== dateFilter.dateRange[1]?.format('YYYY-MM-DD');
    }

    if (modeChanged || rangeChanged) {
      setDateFilter(newValue);
    }
  };

  const handleDownloadProfitabilityReport = async (format) => {
    try {
      const params = {};
      if (dateFilter.dateRange) {
        params.startDate = dateFilter.dateRange[0]?.format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1]?.format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      }
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      params.format = format;
      const response = await apiService.reports.downloadVanProfitabilityReport(params);
      const blob = new Blob([response.data], { type: format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'excel' ? 'van_profitability_report.xlsx' : 'van_profitability_report.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to download profitability report');
    }
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={() => handleDownloadProfitabilityReport('csv')}>Download as CSV</Menu.Item>
      <Menu.Item key="excel" onClick={() => handleDownloadProfitabilityReport('excel')}>Download as Excel</Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Registration',
      dataIndex: 'registration',
      key: 'registration',
      render: (text, record) => (
        <Space>
          <CarOutlined />
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: 'Total Jobs',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      sorter: (a, b) => a.totalJobs - b.totalJobs
    },
    {
      title: 'Completed',
      dataIndex: 'completedJobs',
      key: 'completedJobs',
      render: (completed, record) => (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>{completed} / {record.totalJobs}</Text>
          <Progress 
            percent={Math.round((completed / record.totalJobs) * 100)} 
            size="small"
            status={completed === record.totalJobs ? 'success' : 'active'}
          />
        </Space>
      ),
      sorter: (a, b) => a.completedJobs - b.completedJobs
    },
    {
      title: 'Pending',
      dataIndex: 'pendingJobs',
      key: 'pendingJobs',
      render: (pending) => (
        <Tag color={pending > 0 ? 'warning' : 'success'}>
          {pending}
        </Tag>
      )
    },
    {
      title: 'Mileage',
      dataIndex: 'currentMileage',
      key: 'currentMileage',
      render: (mileage) => `${mileage?.toLocaleString() || 0} miles`,
      sorter: (a, b) => (a.currentMileage || 0) - (b.currentMileage || 0)
    },
    {
      title: 'Fuel Expense',
      dataIndex: 'fuelExpense',
      key: 'fuelExpense',
      render: (expense) => `£${expense?.toFixed(2) || '0.00'}`,
      sorter: (a, b) => (a.fuelExpense || 0) - (b.fuelExpense || 0)
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (revenue) => `£${revenue?.toFixed(2) || '0.00'}`,
      sorter: (a, b) => (a.totalRevenue || 0) - (b.totalRevenue || 0)
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading van data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={fetchVanData}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>Van Reports</Title>
            <Text type="secondary">View and analyze van performance metrics</Text>
          </div>

          <Menu
            mode="horizontal"
            selectedKeys={['van']}
            style={{ marginBottom: '24px' }}
          >
            <Menu.Item key="dashboard" icon={<LineChartOutlined />}>
              <Link to="/reports">Dashboard</Link>
            </Menu.Item>
            <Menu.Item key="financial" icon={<span style={{ fontSize: '16px' }}>£</span>}>
              <Link to="/reports/financial">Financial</Link>
            </Menu.Item>
            <Menu.Item key="revenue" icon={<DollarOutlined />}>
              <Link to="/reports/revenue">Revenue</Link>
            </Menu.Item>
            <Menu.Item key="van" icon={<CarOutlined />}>
              <Link to="/reports/van">Vans</Link>
            </Menu.Item>
            <Menu.Item key="resources" icon={<ClusterOutlined />}>
              <Link to="/reports/resources">Resource Utilization</Link>
            </Menu.Item>
          </Menu>

          {/* Filters Row */}
          <Card title="Filters" style={{ marginBottom: 16 }}>
            <Row gutter={16} align="middle" style={{ flexWrap: 'wrap' }}>
              {/* Date Filter */}
              <Col xs={24} sm={12} md={10} lg={8} xl={8}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>Date Range:</Text>
                  <DateRangeFilter value={dateFilter} onChange={handleDateFilterChange} />
                </Space>
              </Col>
              {/* Status Filter */}
              <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>Van Status:</Text>
                  <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }}>
                    <Select.Option value="all">All Statuses</Select.Option>
                    <Select.Option value="available">Available</Select.Option>
                    <Select.Option value="in-use">In Use</Select.Option>
                    <Select.Option value="maintenance">Maintenance</Select.Option>
                    <Select.Option value="not available">Not Available</Select.Option>
                  </Select>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Stats Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Total Vans"
                  value={stats.totalVans}
                  prefix={<CarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Completed Jobs"
                  value={stats.completedJobs}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Total Jobs"
                  value={stats.totalJobs}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Van Status Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={vanStatusDistribution}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    >
                      {vanStatusDistribution.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={
                            entry.name === 'Available' ? '#52c41a' :
                            entry.name === 'In-use' ? '#1890ff' :
                            entry.name === 'Maintenance' ? '#faad14' :
                            entry.name === 'Not Available' ? '#ff4d4f' :
                            '#d9d9d9'
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Van Job Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={vanJobDistribution}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" name="Completed Jobs" fill="#52c41a" />
                    <Bar dataKey="pending" name="Pending Jobs" fill="#faad14" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Vans Table */}
          <Card
            title="Van Performance"
            extra={
              <Space>
                <Dropdown overlay={downloadMenu} placement="bottomRight">
                  <Button type="primary" icon={<DownloadOutlined />}>
                    Download Profitability Report <DownOutlined />
                  </Button>
                </Dropdown>
                <Button type="primary" icon={<DownloadOutlined />}>
                  Export to Excel
                </Button>
              </Space>
            }
          >
            {vans.length === 0 ? (
              <Alert
                message="No Data"
                description="No van data available for the selected filters."
                type="info"
                showIcon
              />
            ) : (
              <Table
                columns={columns}
                dataSource={vans}
                rowKey="_id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} vans`
                }}
              />
            )}
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default VanReport; 